/**
 * Felix Carousel (Light DOM)
 *
 * Reusable carousel engine that enhances existing markup in Light DOM.
 * Uses native scrolling + scroll-snap (no cloning, no Shadow DOM).
 *
 * @package Felix
 * @since 1.0.0
 */

class FelixCarousel extends HTMLElement {
	connectedCallback() {
		if (this._isConnected) {
			return
		}

		this._isConnected = true
		this._rafId = 0

		this.containerSelector = this.dataset.carouselContainer || '[data-carousel-container]'
		this.itemSelector = this.dataset.carouselItem || '[data-carousel-item]'
		this.prevSelector = this.dataset.carouselPrev || '[data-carousel-prev]'
		this.nextSelector = this.dataset.carouselNext || '[data-carousel-next]'
		this.mobileOnly = this.dataset.carouselMobileOnly === 'true'
		this.enableLeftOverflowState = this.dataset.carouselLeftOverflow === 'true'
		this.startLeaveThreshold = Number.parseFloat(this.dataset.carouselStartLeaveThreshold || '18')
		this.startReturnThreshold = Number.parseFloat(this.dataset.carouselStartReturnThreshold || '10')
		this.isAtStart = true
		this.isReturningToStart = false
		this.returnToStartTimer = 0

		this.containerEl = this.querySelector(this.containerSelector)
		this.prevBtn = this.querySelector(this.prevSelector)
		this.nextBtn = this.querySelector(this.nextSelector)

		if (!this.containerEl) {
			return
		}

		this.onPrev = this.onPrev.bind(this)
		this.onNext = this.onNext.bind(this)
		this.onScroll = this.onScroll.bind(this)
		this.onScrollEnd = this.onScrollEnd.bind(this)
		this.onResize = this.onResize.bind(this)
		this.onBreakpointChange = this.onBreakpointChange.bind(this)

		if (this.prevBtn) {
			this.prevBtn.addEventListener('click', this.onPrev)
		}
		if (this.nextBtn) {
			this.nextBtn.addEventListener('click', this.onNext)
		}
		this.containerEl.addEventListener('scroll', this.onScroll, { passive: true })
		this.containerEl.addEventListener('scrollend', this.onScrollEnd)
		window.addEventListener('resize', this.onResize, { passive: true })

		if (this.mobileOnly && window.matchMedia) {
			this.breakpointMql = window.matchMedia('(max-width: 63.99rem)')
			if (this.breakpointMql.addEventListener) {
				this.breakpointMql.addEventListener('change', this.onBreakpointChange)
			} else if (this.breakpointMql.addListener) {
				this.breakpointMql.addListener(this.onBreakpointChange)
			}
		}

		this.applyMode()
	}

	disconnectedCallback() {
		if (this.prevBtn) {
			this.prevBtn.removeEventListener('click', this.onPrev)
		}
		if (this.nextBtn) {
			this.nextBtn.removeEventListener('click', this.onNext)
		}
		if (this.containerEl) {
			this.containerEl.removeEventListener('scroll', this.onScroll)
			this.containerEl.removeEventListener('scrollend', this.onScrollEnd)
		}
		window.removeEventListener('resize', this.onResize)
		this.clearReturnToStartTimer()

		if (this.breakpointMql) {
			if (this.breakpointMql.removeEventListener) {
				this.breakpointMql.removeEventListener('change', this.onBreakpointChange)
			} else if (this.breakpointMql.removeListener) {
				this.breakpointMql.removeListener(this.onBreakpointChange)
			}
		}

	}

	onBreakpointChange() {
		this.applyMode()
	}

	onResize() {
		this.applyMode()
	}

	onScroll() {
		if (this.isReturningToStart) {
			this.scheduleReturnToStartFallback()
			this.scheduleNavUpdate()
			return
		}

		this.updateStartState()
		this.scheduleNavUpdate()
	}

	onScrollEnd() {
		if (!this.isReturningToStart) {
			return
		}

		if (this.containerEl.scrollLeft <= this.startReturnThreshold) {
			this.finishReturnToStart()
			return
		}

		this.scheduleReturnToStartFallback()
	}

	onPrev(e) {
		e.preventDefault()
		if (!this.isActive) {
			return
		}
		this.scrollByStep(-1)
	}

	onNext(e) {
		e.preventDefault()
		if (!this.isActive) {
			return
		}
		this.scrollByStep(1)
	}

	applyMode() {
		const isMobileViewport = this.breakpointMql ? this.breakpointMql.matches : true
		const modeAllowsCarousel = !this.mobileOnly || isMobileViewport
		const hasScrollableContent = this.hasScrollableContent()
		this.isActive = modeAllowsCarousel && hasScrollableContent
		this.setButtonsVisible(this.isActive)
		this.isReturningToStart = false
		this.clearReturnToStartTimer()

		if (this.isActive) {
			this.containerEl.setAttribute('data-carousel-active', 'true')
			this.enableButtons()
			this.updateStartState(true)
			this.updateNavState()
		} else {
			this.containerEl.removeAttribute('data-carousel-active')
			this.disableButtons()
			this.containerEl.scrollLeft = 0
			this.setStartState(true)
		}
	}

	setButtonsVisible(isVisible) {
		if (this.prevBtn) {
			this.prevBtn.hidden = !isVisible
			this.prevBtn.style.display = isVisible ? '' : 'none'
		}
		if (this.nextBtn) {
			this.nextBtn.hidden = !isVisible
			this.nextBtn.style.display = isVisible ? '' : 'none'
		}
	}

	hasScrollableContent() {
		if (!this.containerEl) {
			return false
		}

		return this.containerEl.scrollWidth > this.containerEl.clientWidth
	}

	getMaxScrollLeft() {
		if (!this.containerEl) {
			return 0
		}
		return Math.max(0, this.containerEl.scrollWidth - this.containerEl.clientWidth)
	}

	enableButtons() {
		if (this.prevBtn) {
			this.prevBtn.disabled = false
			this.prevBtn.removeAttribute('aria-disabled')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = false
			this.nextBtn.removeAttribute('aria-disabled')
		}
	}

	disableButtons() {
		if (this.prevBtn) {
			this.prevBtn.disabled = true
			this.prevBtn.setAttribute('aria-disabled', 'true')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = true
			this.nextBtn.setAttribute('aria-disabled', 'true')
		}
	}

	scheduleNavUpdate() {
		if (!this.isActive) {
			return
		}
		if (this._rafId) {
			return
		}
		this._rafId = window.requestAnimationFrame(() => {
			this._rafId = 0
			this.updateNavState()
		})
	}

	updateNavState() {
		if (!this.isActive || !this.containerEl) {
			this.disableButtons()
			return
		}

		const maxScrollLeft = this.getMaxScrollLeft()
		if (maxScrollLeft <= 0) {
			this.setButtonsVisible(false)
			this.disableButtons()
			return
		}
		this.setButtonsVisible(true)
		const current = this.containerEl.scrollLeft
		const atStart = current <= this.startReturnThreshold
		const atEnd = current >= maxScrollLeft

		if (this.prevBtn) {
			this.prevBtn.disabled = atStart
			this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = atEnd
			this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false')
		}
	}

	updateStartState(force = false) {
		if (!this.enableLeftOverflowState || !this.containerEl) {
			return
		}

		const current = this.containerEl.scrollLeft
		let nextState = this.isAtStart

		if (this.isAtStart) {
			if (current > this.startLeaveThreshold) {
				nextState = false
			}
		} else if (current <= this.startReturnThreshold) {
			nextState = true
		}

		if (force) {
			nextState = current <= this.startReturnThreshold
		}

		if (nextState !== this.isAtStart || force) {
			this.setStartState(nextState)
		}
	}

	setStartState(isAtStart) {
		if (this.isAtStart === isAtStart && this.getAttribute('data-carousel-at-start') === (isAtStart ? 'true' : 'false')) {
			return
		}
		this.isAtStart = isAtStart
		this.setAttribute('data-carousel-at-start', isAtStart ? 'true' : 'false')
	}

	getStepSize() {
		const configuredStep = parseFloat(this.dataset.carouselStep || '')
		if (!Number.isNaN(configuredStep) && configuredStep > 0) {
			return configuredStep
		}

		const items = this.querySelectorAll(this.itemSelector)
		if (items.length >= 2) {
			return Math.abs(items[1].offsetLeft - items[0].offsetLeft) || items[0].offsetWidth
		}
		if (items.length === 1) {
			return items[0].offsetWidth
		}

		return this.containerEl.clientWidth
	}

	clearReturnToStartTimer() {
		if (!this.returnToStartTimer) {
			return
		}

		window.clearTimeout(this.returnToStartTimer)
		this.returnToStartTimer = 0
	}

	scheduleReturnToStartFallback() {
		this.clearReturnToStartTimer()
		this.returnToStartTimer = window.setTimeout(() => {
			this.returnToStartTimer = 0
			if (!this.isReturningToStart) {
				return
			}
			if (this.containerEl.scrollLeft <= this.startReturnThreshold) {
				this.finishReturnToStart()
			}
		}, 120)
	}

	finishReturnToStart() {
		this.isReturningToStart = false
		this.clearReturnToStartTimer()
		this.setStartState(true)
		this.updateNavState()
	}

	// A fixed step skips cards when widths differ (e.g. card-inset--wide), so mixed
	// tracks aim at the next or previous item edge instead.
	getMixedWidthTarget(direction) {
		if (this.dataset.carouselStep) {
			return null
		}
		const items = [...this.querySelectorAll(this.itemSelector)]
		if (items.length < 2 || items.every((item) => item.offsetWidth === items[0].offsetWidth)) {
			return null
		}

		const current = this.containerEl.scrollLeft
		const edge = this.containerEl.getBoundingClientRect().left
		const offsets = items.map((item) => current + item.getBoundingClientRect().left - edge)
		const target =
			direction > 0 ? offsets.find((o) => o > current + 1) : [...offsets].reverse().find((o) => o < current - 1)

		return target === undefined ? null : target
	}

	scrollByStep(direction) {
		const current = this.containerEl.scrollLeft
		const mixedTarget = this.getMixedWidthTarget(direction)
		const left = mixedTarget !== null ? mixedTarget : current + direction * this.getStepSize()

		if (direction < 0 && this.enableLeftOverflowState) {
			const computed = window.getComputedStyle(this.containerEl)
			const leftBleed = Number.parseFloat(computed.paddingLeft || '0') || 0
			const shouldReturnToStart = left <= leftBleed + this.startReturnThreshold

			if (shouldReturnToStart) {
				this.isReturningToStart = true
				this.clearReturnToStartTimer()

				// Resolve the smooth scroll target against the contained layout so the
				// browser can return to the true start position instead of the bleed offset.
				window.requestAnimationFrame(() => {
					this.setStartState(true)
					this.containerEl.scrollTo({
						left: 0,
						behavior: 'smooth',
					})
					this.setStartState(false)
					this.scheduleReturnToStartFallback()
				})
				return
			}
		}

		this.containerEl.scrollTo({
			left,
			behavior: 'smooth',
		})
	}
}

customElements.define('felix-carousel', FelixCarousel)
